/**
 * FieldGroupSection -- renders a connector field-group as a flat, always-open
 * section: a heading, an optional description, and the group's fields. The
 * schema's `collapsible` / `default_collapsed` flags are intentionally ignored
 * (Configure-step redesign #260, decision D3 -- "flatten everything").
 */
import React from 'react';
import * as Icons from 'lucide-react';
import type { ConnectorFieldGroup } from '../../../../types/connector-schema';

/**
 * Fallback lucide icon (kebab name) for each backend field-group `name`, used
 * only when the group has no per-group `icon` from the backend (#898). Since
 * #898, each `ConnectorFieldGroup` carries its own `icon` (seeded from the
 * curated Configuration-tab section icons), so the wizard and Configuration tab
 * share one per-connector source of truth; this name-keyed map remains as a
 * fallback for any group whose `icon` is absent. Unmapped groups fall back to
 * `settings`. (Introduced by #897 as the sole source; demoted to fallback by #898.)
 */
const GROUP_ICONS: Record<string, string> = {
  authentication: 'key-round',
  credentials: 'key-round',
  connection: 'database',
  account_config: 'briefcase',
  accounts: 'briefcase',
  data_config: 'bar-chart-3',
  data_selection: 'bar-chart-3',
  analytics_config: 'bar-chart-3',
  resources: 'bar-chart-3',
  report: 'bar-chart-3',
  extraction: 'bar-chart-3',
  reporting_window: 'calendar-range',
  spreadsheet_config: 'table',
  drive_discovery: 'folder-search',
  cell_ranges: 'file-code',
  month_unpivot: 'columns-3',
  rate_limiting: 'gauge',
  performance: 'gauge',
  output_config: 'file-output',
  advanced: 'sliders-horizontal',
  validation: 'shield-check',
};

// Resolve a kebab-case lucide icon name to its PascalCase key, mirroring the
// helper in the Configuration tab's SectionHeader.tsx / JumpNavStrip.tsx. Kept
// as a pure string transform (rather than returning the icon component
// itself) so the lookup below is a plain property access, not a call whose
// result feeds a JSX tag -- react-hooks/static-components flags a dynamic
// component created during render (#1123).
function iconKey(name: string): string {
  return name.split('-').map((w) => w[0].toUpperCase() + w.slice(1)).join('');
}

interface FieldGroupSectionProps {
  group: ConnectorFieldGroup;
  children: React.ReactNode;
}

export function FieldGroupSection({ group, children }: FieldGroupSectionProps) {
  const iconMap: Record<string, any> = Icons;
  const Icon = iconMap[iconKey(group.icon ?? GROUP_ICONS[group.name] ?? 'settings')] ?? Icons.Settings;
  return (
    <section className="space-y-4 border-t pt-6 first:border-t-0 first:pt-0">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
          <Icon className="h-4 w-4" aria-hidden />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold tracking-tight">{group.label}</h3>
          {group.description && (
            <p className="text-sm text-muted-foreground mt-1">{group.description}</p>
          )}
        </div>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}
